// src/tattoo/SaveShareButton.jsx
import React, { useState } from "react";
import { getOrCreateShareUrlForCurrentProject } from "./share";

function isIOS() {
  return /iP(hone|ad|od)/.test(navigator.userAgent);
}

// iOS Safari can choke on very long URLs (especially hashes).
// Keep this conservative.
const IOS_URL_SOFT_LIMIT = 1800;

async function shareUrlSmart({ title, text, url }) {
  // 1) iOS: if URL is huge, prefer "copy" immediately (share sheet often fails)
  if (isIOS() && url && url.length > IOS_URL_SOFT_LIMIT) {
    try {
      await navigator.clipboard.writeText(url);
      return { ok: true, mode: "copied_long_url" };
    } catch {
      // last resort: prompt
      window.prompt("Copy link:", url);
      return { ok: true, mode: "prompt_long_url" };
    }
  }

  // 2) Native share (must be called from a user click/tap)
  if (navigator.share) {
    try {
      await navigator.share({ title, text, url });
      return { ok: true, mode: "native_share" };
    } catch (e) {
      // user cancel is not an error worth surfacing
      return { ok: false, mode: "share_failed", error: e };
    }
  }

  // 3) Clipboard fallback
  try {
    await navigator.clipboard.writeText(url);
    return { ok: true, mode: "copied" };
  } catch (e) {
    window.prompt("Copy link:", url);
    return { ok: true, mode: "prompt" };
  }
}

export default function SaveShareButton({
  className = "tt-btn",
  getActiveLayerId,
}) {
  const [busy, setBusy] = useState(false);

  async function handleClick(e) {
    e.preventDefault();
    e.stopPropagation();
  
    if (busy) return;
    setBusy(true);
  
    try {
      // Use the canonical share-URL helper. It serializes the project,
      // captures the current camera, defaults to `r: true` (read-only
      // viewer mode), and returns a resilient short link with a legacy
      // hash fallback. The previous implementation called
      // `exportProjectHashAsync()` + `forceViewerUrl()` directly but
      // never imported either, so every tap threw a silent ReferenceError
      // that the catch block swallowed — that was the "Share button does
      // nothing" symptom from iOS testing.
      const url = await getOrCreateShareUrlForCurrentProject();

      if (!url) {
        console.warn("Share failed: no URL produced");
        return;
      }

      const res = await shareUrlSmart({
        title: "Tattune design",
        text: "Check this Tattune design:",
        url,
      });

      if (res.ok && (res.mode === "copied" || res.mode === "copied_long_url")) {
        // hook into your toast system if you have one
        console.log("Link copied");
      }
    } catch (err) {
      console.warn("Share failed", err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <button
      type="button"
      className={className}
      onClick={handleClick}
      disabled={busy}
    >
      {busy ? "Saving…" : "Share"}
    </button>
  );
}